import React, { useState } from "react";
import { FiThumbsUp, FiThumbsDown } from "react-icons/fi";
import { en } from "../i18n/en";
import { useSubmitAccuracyFeedback } from "../hooks/useSubmitAccuracyFeedback";

const MAX_COMMENT_LENGTH = 500;

interface AccuracyFeedbackControlProps {
  documentId: number;
  feedbackSubmittedAt: string | null;
  ocrJobId: number;
}

interface ReadOnlySummaryProps {
  message: string;
}

const ReadOnlySummary: React.FC<ReadOnlySummaryProps> = ({ message }) => (
  <div
    className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800"
    data-testid="feedback-readonly-summary"
    role="status"
  >
    {message}
  </div>
);

export const AccuracyFeedbackControl: React.FC<AccuracyFeedbackControlProps> = ({
  ocrJobId,
  documentId,
  feedbackSubmittedAt,
}) => {
  const copy = en.feedback;
  const [rating, setRating] = useState<0 | 1 | null>(null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");
  const [alreadySubmittedError, setAlreadySubmittedError] = useState(false);

  const mutation = useSubmitAccuracyFeedback({ ocrJobId, documentId });

  if (feedbackSubmittedAt !== null || mutation.isSuccess) {
    return <ReadOnlySummary message={copy.successMessage} />;
  }

  const handleSubmit = () => {
    if (rating === null) {
      return;
    }

    const payload = {
      rating,
      ...(comment.trim() ? { comment: comment.trim() } : {}),
    };

    mutation.mutate(payload, {
      onError: (error: unknown) => {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError?.response?.status === 409) {
          setAlreadySubmittedError(true);
        }
      },
    });
  };

  if (alreadySubmittedError) {
    return <ReadOnlySummary message={copy.alreadySubmitted} />;
  }

  return (
    <div
      className="mt-6 pt-4 border-t border-gray-200"
      data-testid="accuracy-feedback-control"
    >
      <p className="text-sm font-medium text-gray-700 mb-3">{copy.prompt}</p>

      <div className="flex gap-2 mb-3">
        <button
          type="button"
          onClick={() => setRating(1)}
          aria-pressed={rating === 1}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
            rating === 1
              ? "bg-green-600 text-white border-green-600"
              : "bg-white text-gray-700 border-gray-300 hover:border-green-500"
          }`}
          data-testid="feedback-thumbs-up"
        >
          <FiThumbsUp size={14} />
          {copy.thumbsUp}
        </button>

        <button
          type="button"
          onClick={() => setRating(0)}
          aria-pressed={rating === 0}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
            rating === 0
              ? "bg-red-600 text-white border-red-600"
              : "bg-white text-gray-700 border-gray-300 hover:border-red-500"
          }`}
          data-testid="feedback-thumbs-down"
        >
          <FiThumbsDown size={14} />
          {copy.thumbsDown}
        </button>
      </div>

      {!showComment && (
        <button
          type="button"
          onClick={() => setShowComment(true)}
          className="text-xs text-blue-600 underline mb-3 block"
          data-testid="feedback-add-comment-toggle"
        >
          {copy.addComment}
        </button>
      )}

      {showComment && (
        <div className="mb-3">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
            placeholder={copy.commentPlaceholder}
            maxLength={MAX_COMMENT_LENGTH}
            rows={3}
            className="w-full border border-gray-300 rounded-md p-2 text-sm resize-none"
            data-testid="feedback-comment-textarea"
          />
          <p className="text-xs text-gray-500 text-right">
            {comment.length}/{MAX_COMMENT_LENGTH}
          </p>
        </div>
      )}

      {mutation.isError && !alreadySubmittedError && (
        <p className="text-red-600 text-xs mb-2" role="alert" data-testid="feedback-error">
          Something went wrong. Please try again.
        </p>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={rating === null || mutation.isPending}
        className="px-4 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium disabled:opacity-50"
        data-testid="feedback-submit"
      >
        {mutation.isPending ? copy.submitting : copy.submit}
      </button>
    </div>
  );
};
